import React, { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../auth/AuthContext'
import { ioGet } from './api'
import { btnPrimary, cardClass, statusBadge } from './ui'

export default function CampaignList({ kind }) {
  const { user, isAdmin } = useAuth()
  const [params] = useSearchParams()
  const resolvedKind = kind || params.get('kind') || defaultKind(user)
  const [rows, setRows] = useState([])
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [archived, setArchived] = useState(false)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError('')
      try {
        const q = new URLSearchParams()
        if (resolvedKind) q.set('kind', resolvedKind)
        if (archived) q.set('archived', '1')
        const data = await ioGet(`/api/io/campaigns?${q.toString()}`)
        if (!cancelled) setRows(data.campaigns || [])
      } catch (e) {
        if (!cancelled) setError(e.message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [resolvedKind, archived])

  const title = resolvedKind === 'house_message'
    ? 'Campaigns'
    : resolvedKind === 'audience'
      ? 'Audience campaigns'
      : 'Campaigns'
  const createTo = resolvedKind === 'house_message'
    ? '/campaigns/venue/new'
    : resolvedKind === 'audience'
      ? '/audience/new'
      : '/campaigns/new'

  return (
    <div className="max-w-5xl mx-auto space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{title}</h1>
          <p className="text-sm text-gray-500">Draft, hold, and booked insertions in this workspace.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500">
            <input type="checkbox" className="mr-1" checked={archived} onChange={(e) => setArchived(e.target.checked)} />
            Archived
          </label>
          <Link to={createTo} className={btnPrimary}>Create</Link>
        </div>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      {loading && <p className="text-sm text-gray-500">Loading…</p>}
      <div className={`${cardClass} overflow-hidden`}>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900 text-left text-gray-500">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Advertiser</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Flight</th>
              {isAdmin && <th className="px-4 py-2">Control</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className="border-t border-gray-200 dark:border-gray-700">
                <td className="px-4 py-2">
                  <Link to={`/campaigns/${c.id}`} className="text-blue-600 dark:text-blue-400 font-medium">
                    {c.campaignName || `Campaign ${c.id}`}
                  </Link>
                </td>
                <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{c.advertiserName}</td>
                <td className="px-4 py-2"><span className={statusBadge(c.status)}>{c.status}</span></td>
                <td className="px-4 py-2 text-gray-500">{fmtDate(c.startDate)} → {fmtDate(c.endDate) || 'open'}</td>
                {isAdmin && (
                  <td className="px-4 py-2 text-xs text-gray-500">{c.broadsign?.reservationId || '—'}</td>
                )}
              </tr>
            ))}
            {!loading && !rows.length && (
              <tr><td className="px-4 py-8 text-gray-500" colSpan={5}>No campaigns yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function defaultKind(user) {
  if (user?.userType === 'venue') return 'house_message'
  return 'guaranteed'
}

function fmtDate(v) {
  if (!v) return ''
  const d = new Date(v)
  if (Number.isNaN(d.getTime())) return String(v).slice(0, 10)
  return d.toISOString().slice(0, 10)
}
